import { useEffect, useState, useContext } from 'react';
import { toast } from '../lib/toast.js';
import { useParams } from 'react-router-dom';
import GlobalContext from "../context/GlobalContext.js";
import getValidateOnboardingToken from '../functions/API/getValidateOnboardingToken';
import postCompleteOnboardingRegistration from '../functions/API/postCompleteOnboardingRegistration';
import PasswordStrengthChecker from '../components/PasswordStrengthChecker';
import { darkenColor } from '../functions/colorUtils.js';
import { UserPlus, X, Eye, EyeOff, CheckCircle2, XCircle, ArrowRight, ArrowLeft } from 'lucide-react';

const STEPS = [
    { key: 'email', question: 'Confirm your email address', description: 'This is the email address your account will be created with' },
    { key: 'password', question: 'Create a password', description: 'Choose a strong password to secure your account' },
];

export default function OnboardingAccept() {
    const { token } = useParams();
    const { settings } = useContext(GlobalContext);
    const primaryColor = settings?.primary_hex_colour || '#813468';
    const primaryDark = darkenColor(primaryColor);

    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [inviteData, setInviteData] = useState(null);

    const [step, setStep] = useState(0);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [passwordSecure, setPasswordSecure] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        async function validate() {
            const response = await getValidateOnboardingToken(token);
            if (response.response?.ok) {
                setInviteData(response.data);
                setTokenValid(true);
            }
            setValidating(false);
        }
        validate();
    }, [token]);

    function next() {
        if (step === 0) { setStep(1); return; }
        submitRegistration();
    }

    function back() {
        if (step > 0) setStep(s => s - 1);
    }

    async function submitRegistration() {
        if (!passwordSecure) { toast.error('Your password does not meet the minimum requirements'); return; }
        if (password !== confirmPassword) { toast.error("Passwords don't match"); return; }
        setLoading(true);
        const response = await postCompleteOnboardingRegistration(token, password, confirmPassword);
        if (response.response?.ok) {
            setSuccess(true);
            setTimeout(() => { window.location.href = '/login'; }, 3000);
        } else {
            const errorMsg = response.data?.message || 'Registration failed. Please try again.';
            if (typeof errorMsg === 'object') {
                Object.values(errorMsg).flat().forEach(m => toast.error(m));
            } else {
                toast.error(errorMsg);
            }
        }
        setLoading(false);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') { e.preventDefault(); next(); }
    }

    const inputClass = 'w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow';

    function GradientContent({ className }) {
        return (
            <div className={className}>
                <a
                    href="/login"
                    className="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium hover:text-white transition-colors mb-5"
                >
                    <X size={16} strokeWidth={2.5} />
                    Exit
                </a>

                <div className="flex gap-1.5 mb-8">
                    {STEPS.map((_, i) => (
                        <div
                            key={i}
                            className="flex-1 h-1 rounded-full transition-colors duration-300"
                            style={{ backgroundColor: success || i <= step ? '#fff' : 'rgba(255,255,255,0.25)' }}
                        />
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <UserPlus size={22} className="text-white" strokeWidth={1.8} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Welcome{inviteData ? `, ${inviteData.first_name}` : ''}</h1>
                        <p className="text-white/60 text-sm mt-0.5">Create your account</p>
                    </div>
                </div>

                <p className="hidden lg:block mt-6 text-white/60 text-sm">
                    Step {step + 1} of {STEPS.length}
                </p>
            </div>
        );
    }

    // Loading / invalid states — simple centered
    if (validating) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F2F2F7' }}>
                <div className="text-center">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-full animate-spin" style={{ borderWidth: '3px', borderColor: `${primaryColor}30`, borderTopColor: primaryColor, borderStyle: 'solid' }} />
                    <p className="text-sm text-gray-500">Validating your invite...</p>
                </div>
            </div>
        );
    }

    if (!tokenValid) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#F2F2F7' }}>
                <div className="w-full max-w-sm text-center">
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                        <XCircle size={32} className="text-red-500" strokeWidth={1.5} />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid or expired link</h2>
                    <p className="text-sm text-gray-500 mb-8">
                        This invite link is no longer valid. It may have expired or already been used. Please contact your housing provider for assistance.
                    </p>
                    <a
                        href="/login"
                        className="w-full flex items-center justify-center rounded-2xl py-4 text-base font-semibold text-white transition-opacity hover:opacity-90"
                        style={{ backgroundColor: primaryColor }}
                    >
                        Go to login
                    </a>
                </div>
            </div>
        );
    }

    const formContent = success ? (
        <>
            <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 size={32} className="text-green-600" strokeWidth={1.5} />
                </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-1.5">Account created</h2>
            <p className="text-sm text-gray-500 text-center mb-8">
                Your account has been set up. Redirecting you to sign in...
            </p>
            <a
                href="/login"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
            >
                Sign In
            </a>
        </>
    ) : (
        <>
            <h2 className="text-xl font-bold text-gray-900 mb-1.5">{STEPS[step].question}</h2>
            <p className="text-sm text-gray-500 mb-6">{STEPS[step].description}</p>

            {step === 0 ? (
                <input
                    type="email"
                    value={inviteData?.email || ''}
                    disabled
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-sm text-gray-500"
                />
            ) : (
                <div className="space-y-3">
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="New password"
                            className={`${inputClass} pr-12`}
                            autoFocus
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    <div className="relative">
                        <input
                            type={showConfirm ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Confirm password"
                            className={`${inputClass} pr-12`}
                        />
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                            {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    <PasswordStrengthChecker password={password} passwordSecure={setPasswordSecure} />
                </div>
            )}

            <div className="mt-6 flex gap-3">
                {step > 0 && (
                    <button
                        onClick={back}
                        className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl text-sm font-semibold border-2 transition-opacity hover:opacity-80"
                        style={{ borderColor: primaryColor, color: primaryColor }}
                    >
                        <ArrowLeft size={16} />
                        Back
                    </button>
                )}
                <button
                    onClick={next}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: primaryColor }}
                >
                    {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : step === 0 ? (
                        <>Next <ArrowRight size={16} /></>
                    ) : (
                        'Create Account'
                    )}
                </button>
            </div>

            <p className="mt-8 text-center text-sm text-gray-500">
                Already have an account?{' '}
                <a href="/login" className="font-semibold hover:opacity-80" style={{ color: primaryColor }}>
                    Sign in
                </a>
            </p>
        </>
    );

    return (
        <>
            {/* Mobile */}
            <div className="lg:hidden min-h-screen flex flex-col" style={{ backgroundColor: '#F2F2F7' }}>
                <div className="w-full flex-shrink-0" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}>
                    <GradientContent className="px-5 pt-5 pb-12" />
                </div>
                <div className="flex-1 -mt-6 relative z-10">
                    <div className="bg-[#F2F2F7] rounded-t-[1.5rem] min-h-full">
                        <div className="px-5 pt-7 pb-12">{formContent}</div>
                    </div>
                </div>
            </div>

            {/* Desktop */}
            <div className="hidden lg:flex min-h-screen" style={{ backgroundColor: '#F2F2F7' }}>
                <div className="w-1/2 flex items-center justify-center p-12" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}>
                    <GradientContent className="w-full max-w-md" />
                </div>
                <div className="w-1/2 flex items-center justify-center p-12">
                    <div className="w-full max-w-md">{formContent}</div>
                </div>
            </div>
        </>
    );
}
